<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { DonationAtCart } from '../../../../types';
import { useAToast } from '@Atoms';
import type { Donation } from '@DonationModule';
import { IndexDonationsCategoriesService } from '@DonationModule';
import { useForm } from '@FormModule';
import useCartStore from '../../../../store';
import { CART_STEPS } from '../../../../types';
import { UpdateCartDonationService } from '../../../../services';
import { useCartSteps } from '../../../../composables';
import getSanitizedPayload from './getSanitizedPayload.helper';
import useFields from './useFields';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { cartData, isDonation, isIntake, loading, stepState } = storeToRefs(useCartStore());
const { isIntakeInWithNoPayment } = useCartStore();

const { currentSchemaModels, fieldsSchema, formError, setForm } = useFields();
const {
  INTAKE_CART_MANUAL_STEPS,
  INTAKE_CART_MANUAL_STEPS_SIGNATURE_ONLY,
  currentStep,
  nextStep,
  previousStep,
  setOutcomeFlow,
} = useCartSteps();
const { fieldsValidation } = useForm();
const { notify, TOAST_TYPES } = useAToast();

const categories = ref<string[]>([]);
const stepLoader = ref(true);

const donations = computed((): Donation[] => cartData.value?.donations ?? []);

const nextStepButton = computed(() => ({
  icon: nextStep.value.step ? nextStep.value.step.header.icon : '',
  label: 'common.nextStep',
}));

onMounted(() => onMountStep());

// EVENTS
async function onMountStep() {
  stepLoader.value = true;
  currentStep.value = CART_STEPS.DONATION;

  await fetchDonationCategories();
}

function onUpdateDonationRequest() {
  const hasErrors = fieldsValidation(fieldsSchema.value, currentSchemaModels.value);
  if (hasErrors) return (formError.value = true);

  loading.value = true;
  stepLoader.value = true;
  formError.value = false;

  const TOAST_ERROR_ADDING_TO_CART = {
    message: 'donations.errors.unableToAddDonationToCart.message',
    title: 'donations.errors.unableToAddDonationToCart.title',
    type: TOAST_TYPES.ERROR,
  };

  const PAYLOAD: DonationAtCart = {
    ...(getSanitizedPayload(currentSchemaModels.value) as DonationAtCart),
  };

  UpdateCartDonationService(PAYLOAD)
    .then((data) => {
      cartData.value = data;
      stepState.value![CART_STEPS.DONATION] = PAYLOAD;
      onNextStep();
    })
    .catch((error) => notify(TOAST_ERROR_ADDING_TO_CART) && console.error(error))
    .finally(() => {
      loading.value = false;
      stepLoader.value = false;
    });
}

function onNextStep() {
  const checkInWithNoPayment = isIntakeInWithNoPayment(cartData.value);
  const hasDonations = cartData.value?.donations_details && cartData.value.donations_details.length > 0;

  // prettier-ignore
  const nextStepToGo = hasDonations ? CART_STEPS.PAYMENT_METHOD
    : checkInWithNoPayment ? CART_STEPS.COMPLETE_CHECK_IN
    : CART_STEPS.PAYMENT_METHOD;

  if (isIntake.value) {
    // prettier-ignore
    const flowToSet = hasDonations ? INTAKE_CART_MANUAL_STEPS
      : checkInWithNoPayment ? INTAKE_CART_MANUAL_STEPS_SIGNATURE_ONLY
      : [CART_STEPS.ADD_PEOPLE, CART_STEPS.CHECKOUT_METHOD, CART_STEPS.DONATION, CART_STEPS.PAYMENT_METHOD];

    setOutcomeFlow(flowToSet, 'contact');
  }

  emits('onStepUpdate', nextStepToGo);

  loading.value = false;
}

// SERVICES
async function fetchDonationCategories() {
  const TOAST_ERROR = {
    message: 'errors.generics.fetchIssue.message',
    title: 'errors.generics.fetchIssue.title',
    type: TOAST_TYPES.ERROR,
  };

  await IndexDonationsCategoriesService()
    .then((data: string[]) => {
      categories.value = data;

      setForm(data, donations.value);
    })
    .catch((error) => notify(TOAST_ERROR) && console.error(error))
    .finally(() => (stepLoader.value = false));
}
</script>

<template>
  <div>
    <main class="max-w-[700px] pt-2">
      <FormTemplate
        :formError
        :schema="fieldsSchema"
      >
        <AFormError :formError />
      </FormTemplate>
    </main>

    <footer class="flex justify-between pt-4">
      <Button
        v-if="previousStep"
        outlined
        class="af-button af-button-success-outlined w-fit"
        icon="pi pi-arrow-left"
        :label="$t('common.back')"
        @click="emits('onStepUpdate', previousStep)"
      />

      <div class="flex w-full justify-end">
        <div class="ml-auto flex items-center gap-2">
          <Button
            v-if="!isDonation"
            class="af-button ml-auto"
            iconPos="right"
            severity="secondary"
            :disabled="loading || stepLoader"
            :label="$t('components.forms.button.skip')"
            @click="onNextStep"
          />

          <Button
            class="af-button af-button-success"
            iconPos="right"
            :disabled="loading || stepLoader"
            :label="$t(nextStepButton.label)"
            @click="onUpdateDonationRequest"
          >
            <template #icon>
              <FaIcon
                v-if="nextStepButton.icon"
                class="mr-2"
                :icon="$icons[nextStepButton.icon]"
              />
            </template>
          </Button>
        </div>
      </div>
    </footer>
  </div>
</template>
